import { Input, StyledButton } from "igfd-ui";

import React, { useEffect, useState } from "react";

import {
  Box,
  IconButton,
  Modal,
  Stack,
  Typography,
  styled,
} from "@mui/material";

import { useSnackbar } from "igfd-commons/src/snackbar";

import { ICON_SIZE } from "@dashboard/components/common/StreamlineIcon";
import { CloseIcon } from "@dashboard/components/common/icons";

import { useHubSpotFormSubmit } from "./useHubSpotFormSubmit";

export interface FieldConfig {
  name: string;
  label: string;
  type?: "text" | "email";
  defaultValue?: string;
  required?: boolean;
  multiline?: boolean;
}

interface HubSpotFormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  formId: string;
  fields: FieldConfig[];
  hiddenFields?: { name: string; value: string }[];
  /** Hidden fields derived from form values at submit time */
  deriveHiddenFields?: (
    values: Record<string, string>
  ) => { name: string; value: string }[];
  submitLabel?: string;
  fieldPrefix?: string;
}

export const HubSpotFormDialog = ({
  open,
  onClose,
  title,
  description,
  formId,
  fields,
  hiddenFields,
  deriveHiddenFields,
  submitLabel = "Submit",
  fieldPrefix,
}: HubSpotFormDialogProps) => {
  const { submit, submitting } = useHubSpotFormSubmit(formId);
  const snackbar = useSnackbar();

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ""]))
  );

  useEffect(() => {
    if (open) {
      setValues(
        Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ""]))
      );
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const allFields = [
      ...fields.map((f) => ({
        name: f.name,
        value: `${fieldPrefix ?? ""}${values[f.name] ?? ""}`,
      })),
      ...(hiddenFields ?? []),
      ...(deriveHiddenFields?.(values) ?? []),
    ];
    const ok = await submit(allFields);
    if (ok) {
      snackbar.success("Request submitted successfully");
      onClose();
    } else {
      snackbar.error("Something went wrong. Please try again.");
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBox>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="start"
        >
          <Stack gap={0.5}>
            <Typography variant="h3">{title}</Typography>
            {description && (
              <Typography variant="body1">{description}</Typography>
            )}
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon size={ICON_SIZE.control} />
          </IconButton>
        </Stack>

        <form onSubmit={handleSubmit}>
          <Stack gap={3}>
            {fields.map((field) => (
              <Input
                key={field.name}
                label={field.label}
                type={field.type ?? "text"}
                required={field.required}
                multiline={field.multiline}
                rows={field.multiline ? 3 : undefined}
                fullWidth
                value={values[field.name] ?? ""}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    [field.name]: e.target.value,
                  }))
                }
              />
            ))}

            <StyledButton
              type="submit"
              label={submitLabel}
              variant="contained"
              size="large"
              loading={submitting}
            />
          </Stack>
        </form>
      </ModalBox>
    </Modal>
  );
};

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  maxWidth: "100%",
  maxHeight: "100%",
  overflow: "auto",
  backgroundColor: theme.palette.grey[50],
  borderRadius: 24,
  border: "none",
  outline: "none",
  boxShadow: theme.shadows[10],
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));
